import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { requestPasswordReset } from '../services/passwordResetService';
// Shares LoginPage's stylesheet on purpose. These three screens are one auth card with
// different contents, and giving each its own module is how they drift apart a release
// later. Anything reset-specific (.helper, .hint, .forgotRow) lives there too.
import styles from './LoginPage.module.css';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Pre-filled from the login form when the user got here by failing to sign in.
  const [email,   setEmail]   = useState(location.state?.email || '');
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState('');
  const [logoOk,  setLogoOk]  = useState(true);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await requestPasswordReset(email.trim());
    setLoading(false);

    // The success copy is the backend's, not ours. It is worded to be true whether or not
    // an account exists, and re-writing it here is how that guarantee gets lost.
    if (result.ok) setSent(true);
    else setError(result.message);
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>

        <button className={styles.logo} onClick={() => navigate('/')} aria-label="RepBuddy home">
          {logoOk ? (
            <img
              className={styles.logoImg}
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="RepBuddy"
              onError={() => setLogoOk(false)}
            />
          ) : (
            <div className={styles.logoMark}>R</div>
          )}
          <span className={styles.logoName}>RepBuddy</span>
        </button>

        {sent ? (
          <>
            <p className={styles.tagline}>Check your inbox</p>
            <div className={styles.info} role="status">
              If an account exists for <strong>{email.trim()}</strong>, a password reset link is on its
              way. It expires in an hour and can only be used once.
            </div>
            <p className={styles.helper}>
              Nothing after a few minutes? Check your spam folder, then try again with a
              different address.
            </p>
            <button className={styles.btn} type="button" onClick={() => navigate('/login')}>
              Back to sign in
            </button>
            {/* Lets someone who mistyped their address correct it without a reload. */}
            <p className={styles.toggle}>
              Wrong email?{' '}
              <button className={styles.link} onClick={() => setSent(false)}>
                Try another
              </button>
            </p>
          </>
        ) : (
          <>
            <p className={styles.tagline}>Reset your password</p>
            <p className={styles.helper}>
              Enter the email you sign in with and we'll send you a link to choose a new password.
            </p>

            <form onSubmit={handleSubmit} className={styles.form}>
              <label className={styles.label}>
                Email
                <input
                  className={styles.input}
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  autoFocus
                  autoComplete="email"
                  disabled={loading}
                />
              </label>

              {error && <div className={styles.error} role="alert">{error}</div>}

              <button className={styles.btn} type="submit" disabled={loading || !email.trim()}>
                {loading ? 'Sending…' : 'Send reset link'}
              </button>
            </form>

            <p className={styles.toggle}>
              Remembered it?{' '}
              <button className={styles.link} onClick={() => navigate('/login')}>
                Sign in
              </button>
            </p>
          </>
        )}

        <button className={styles.backHome} onClick={() => navigate('/')}>
          ⬅️ Back to home
        </button>

      </div>
    </div>
  );
}
